<template>
  <div class="grid">
    <TimeAxis
      id="time-axis"
      :startTime="startTime"
      :endTime="endTime"
    ></TimeAxis>
    <div id="future-time-axis">
      <div id="top" class="axis-half"></div>
      <div id="bot" class="axis-half"></div>
    </div>
    <TargetPlot
      class="target-plot"
      :signal="signal"
      :markers="markers"
      :xMax="xMax"
      :xMin="xMin"
      :yMax="yMax"
      :yMin="yMin"
      :mildHypoColor="mildHypoColor"
      :severeHypoColor="severeHypoColor"
      :targetMin="targetMin"
      :targetMax="targetMax"
    ></TargetPlot>
    <div v-if="isZoomedOut" class="zoomed-out-warning">
      MAP Time Axis: Zoomed Out
    </div>
    <div class="future-plot">
      <svg class="future-plot-svg">
        <line
          x1="0"
          :y1="hypoLineHeight"
          x2="100%"
          :y2="hypoLineHeight"
          style="stroke: #818181; stroke-width: 2"
          stroke-dasharray="5 5"
        />
        <rect
          x="0"
          :y="futureTargetY"
          :height="futureTargetHeight"
          width="100%"
          style="fill: #c5eecd"
        />
      </svg>
    </div>
    <div id="y-scale" :style="yScaleStyle">
      <div id="y-max-cont"></div>
      <div id="green" class="y-scale-element">
        <svg
          id="target-upper-label"
          viewBox="0 0 1 1"
          preserveAspectRatio="xMinYMin"
          width="100%"
        >
          <rect
            x="0.05"
            y="-0.20"
            width="0.5"
            height="0.3"
            rx="0.1"
            fill="#C5EECD"
          />
          <text x="0.3" y="0.10" font-size="0.3" text-anchor="middle">
            {{ targetMax }}
          </text>
        </svg>
      </div>
      <div id="y-min-cont" :style="{ backgroundColor: severeHypoColor }"></div>
      <div
        id="light-grey"
        class="y-scale-element"
        :style="{ backgroundColor: mildHypoColor }"
      >
        <svg
          id="target-lower-label"
          viewBox="0 0 1 1"
          preserveAspectRatio="xMinYMin"
          width="100%"
        >
          <rect
            x="0.05"
            y="-0.25"
            width="0.5"
            height="0.45"
            rx="0.1"
            fill="#C5EECD"
          />
          <text x="0.3" y="0.11" font-size="0.3" text-anchor="middle">
            {{ targetMin }}
          </text>
        </svg>
      </div>
    </div>
    <div id="y-limits-div">
      <svg viewBox="0 0 1 1" preserveAspectRatio="xMinYMin" width="100%">
        <text x="0.95" y="0.26" font-size="0.3" text-anchor="end" fill="black">
          {{ yMax }}
        </text>
      </svg>
      <svg
        id="hypo-line-label"
        viewBox="0 0 1 1"
        preserveAspectRatio="xMinYMin"
        width="100%"
        :style="{ top: hypoLineLabelTop }"
      >
        <rect
          x="0.57"
          y="-0.18"
          width="0.43"
          height="0.37"
          rx="0.1"
          fill="#707070"
        />
        <text x="0.8" y="0.11" font-size="0.3" text-anchor="middle">55</text>
      </svg>
      <svg viewBox="0 0 1 1" preserveAspectRatio="xMinYMin meet" id="y-min-svg">
        <text x="0.95" y="0.9" font-size="0.3" text-anchor="end">
          {{ yMin }}
        </text>
      </svg>
    </div>
  </div>
</template>

<script>
import TargetPlot from '../components/TargetPlot'
import TimeAxis from '../components/TimeAxis'

export default {
  name: 'MAPPlot',
  props: {
    signal: {
      type: Array,
      required: true
    },
    markers: {
      type: Array,
      required: false
    },
    xMax: {
      type: Number,
      required: true
    },
    xMin: {
      type: Number,
      required: true
    },
    yMax: {
      type: Number,
      required: true
    },
    yMin: {
      type: Number,
      required: true
    },
    targetMin: {
      type: Number,
      required: true
    },
    targetMax: {
      type: Number,
      required: true
    },
    startTime: {
      type: Date,
      required: true
    },
    endTime: {
      type: Date,
      required: true
    },
    mildHypoColor: {
      type: String,
      required: true
    },
    severeHypoColor: {
      type: String,
      required: true
    }
  },
  components: {
    TargetPlot,
    TimeAxis
  },
  computed: {
    yScaleStyle() {
      let total = this.yMax - this.yMin
      let severeHypo = Math.min(55.0, this.targetMin)
      let darkGray = (severeHypo - this.yMin) / total
      let lightGray = (this.targetMin - severeHypo) / total
      let green = (this.targetMax - this.targetMin) / total
      let top = 1.0 - green - lightGray - darkGray
      return `grid-template-rows: ${this.toPercent(top)} ${this.toPercent(
        green
      )} ${this.toPercent(lightGray)} ${this.toPercent(darkGray)}`
    },
    hypoLineHeight() {
      let total = this.yMax - this.yMin
      return this.toPercent((total - (55 - this.yMin)) / total)
    },
    futureTargetY() {
      let total = this.yMax - this.yMin
      return this.toPercent((total - (this.targetMax - this.yMin)) / total)
    },
    futureTargetHeight() {
      let total = this.yMax - this.yMin
      return this.toPercent((this.targetMax - this.targetMin) / total)
    },
    hypoLineLabelTop() {
      return this.toPercent((this.yMax - 55.0) / (this.yMax - this.yMin))
    },
    isZoomedOut() {
      const durationMs = this.endTime - this.startTime
      const fifteenMinutesMs = 15 * 60 * 1000
      return durationMs > fifteenMinutesMs
    }
  },
  methods: {
    toPercent(fraction) {
      let percent = fraction * 100
      return parseFloat(percent).toFixed(3) + '%'
    }
  }
}
</script>

<style scoped>
.grid {
  display: grid;
  grid-template-rows: 12.5% 87.5%;
  grid-template-columns: 6% 75% 19%;
}
#time-axis {
  grid-row: 1;
  grid-column: 2;
}
#future-time-axis {
  grid-row: 1;
  grid-column: 3;
  background-color: #d8d8d8;
  border-style: solid;
  border-width: thin;
  border-color: #979797;
  display: grid;
  grid-template-rows: 50% 50%;
}
#top {
  grid-row: 1;
  grid-column: 1;
  border-bottom: 1px solid #979797;
}
#bot {
  grid-row: 2;
  grid-column: 1;
}
.target-plot {
  position: relative;
  grid-row: 2;
  grid-column: 2;
}
.future-plot {
  grid-row: 2;
  grid-column: 3;
  background-color: #d1d1d1;
  border: gray;
  border-width: medium;
}
.future-plot-svg {
  height: 100%;
  width: 100%;
}
#y-scale {
  position: relative;
  grid-row: 2;
  grid-column: 1;
  border: 1px solid #979797;
  display: grid;
  grid-template-columns: 100%;
}
#y-max-cont {
  grid-row: 1;
  background-color: #eeeeee;
}
#y-min-cont {
  grid-row: 4;
  background-color: #707070;
  display: grid;
  fill: white;
}
#light-grey {
  grid-row: 3;
  background-color: #c4c4c4;
}
#green {
  grid-row: 2;
  background-color: #c5eecd;
}
#target-upper-label {
  grid-row: 2;
  overflow: visible;
}
#target-lower-label {
  grid-row: 3;
  overflow: visible;
}
#hypo-line-label {
  position: absolute;
  right: 0;
  overflow: visible;
  width: 100%;
}
#y-limits-div {
  grid-row: 2;
  grid-column: 1;
  z-index: 2;
  fill: white;
  position: relative;
}
#y-min-svg {
  position: absolute;
  bottom: 0;
  right: 0;
}
.zoomed-out-warning {
  grid-row: 2;
  grid-column: 2;
  position: relative;
  z-index: 10;
  background-color: #f5d742;
  color: black;
  padding: 4px 12px;
  border: 2px solid black;
  border-radius: 4px;
  font-weight: bold;
  font-size: 14px;
  justify-self: center;
  align-self: start;
  margin-top: 10px;
}
</style>
